import { useCallback, useState } from 'react'

// Step history for the multi-step booking forms (ServicesModal on /home,
// Services on the public pages).
//
// Steps are numbered in flow order and the stack only ever grows upward:
// go(n) drops every step >= n before pushing n. That keeps the history right
// even when the forms' "jump to step" effects run more than once while the
// service settings load, and it means going back to an earlier step and
// forward again starts the later steps fresh.
//
// Back pops one step. Steps still on the stack are meant to stay mounted
// (hidden) so their answers survive a Back: the question wizards and date
// pickers reset themselves when they mount.
//
// go(n, { replace: true }) takes the place of the current step, for steps
// that should be skipped on the way back (e.g. a "finding your match" loader).
export const useStepStack = (initialStep) => {
    const [ stack, setStack ] = useState(() => (initialStep ? [initialStep] : []))

    const go = useCallback((step, { replace = false } = {}) => {
        setStack(prev => {
            const base = replace ? prev.slice(0, -1) : prev
            return [...base.filter(s => s < step), step]
        })
    }, [])

    // Returns false when there is no earlier step, so the caller can leave
    // the form (reopen a picker, or close).
    const back = useCallback(() => {
        if (stack.length <= 1) return false
        setStack(prev => prev.slice(0, -1))
        return true
    }, [stack.length])

    const current = stack[stack.length - 1]
    const isMounted = useCallback((step) => stack.includes(step), [stack])

    return { current, stack, go, back, isMounted }
}
